"use client"

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { convertUTCToLocalTime } from '@/lib/utils/timeUtils'
import { formatNumeric, formatDuration } from '@/lib/utils/stringUtils'
import { formatPercentage, toCents } from '@/lib/utils/revenueUtils'
import { cn } from '@/lib/utils'
import type { PeriodSeries, PeriodWindow } from '@/hooks/usePreviousPeriodMetrics'
import { ChartCard, ChartEmpty } from '@/components/shared/charts/ChartCard'
import { TrendLineChart } from '@/components/shared/charts/TrendLineChart'
import { PeriodBarChart } from '@/components/shared/charts/PeriodBarChart'
import { PREV_LINE, type PeriodPoint } from '@/components/shared/charts/chartCardParts'
import { formatSignedDelta, formatWindowRange, periodSeriesLabels } from '@/components/shared/charts/chartCardFormat'
import { SegmentedControl } from '@/components/shared/layout'
import {
  computePeriodDelta,
  computePointDelta,
  type PeriodTotals,
} from '@/lib/utils/previousPeriod'

/**
 * Analytics Overview traffic block (Paper "Analytics · Overview · Split charts"):
 * a KPI strip of the secondary metrics, then two chart cards side by side,
 * never one chart with two units (no dual axis). "Unique visitors" is a line
 * card with a Visitors | Views switch and the previous window dashed;
 * "Revenue" is a bar card (money is bars). Without revenue connected the
 * traffic card goes full width and a quiet "Connect Stripe" line follows.
 */

/** Normalize API bucket fields so sessions/views/revenue can join across formats */
function rawBucket(row: any): string {
  return String(row?.bucket || row?.granularity || row?.time_bucket || row?.date || row?.created_at || '')
}

function dayKey(raw: string): string {
  if (!raw) return ''
  const sliced = raw.slice(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(sliced)) return sliced
  const d = new Date(raw)
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
  return raw
}

const HOURLY_PERIODS = ['PAST_24_H', '1d', 'YESTERDAY', 'TODAY']

/**
 * Join key for a bucket. Hourly periods key by hour: keying them by day gave
 * every hour of a day that whole day's revenue (24 hours showed a row of
 * identical bars and summed to more than the KPI).
 */
export function bucketKey(raw: string, period: string): string {
  if (!HOURLY_PERIODS.includes(period)) return dayKey(raw) || raw
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2})/)
  if (m) return `${m[1]}T${m[2]}`
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? raw : d.toISOString().slice(0, 13)
}

/**
 * Cents from a trend / attribution revenue row.
 * API trend rows ship `total_revenue` in cents (integer); attribution may use
 * amount_cents / dollar floats — defer to shared toCents for those shapes.
 */
function rowCents(row: any): number {
  if (!row) return 0
  if (row.amount_cents != null || row.total_amount_cents != null) {
    return toCents(row)
  }
  // Trend aggregates: total_revenue is cents
  if (row.total_revenue != null && row.total_revenue !== '') {
    const n = typeof row.total_revenue === 'string'
      ? parseFloat(row.total_revenue)
      : Number(row.total_revenue)
    if (!Number.isFinite(n)) return 0
    return Math.round(n)
  }
  return toCents(row)
}

/** Prefer trends.revenue → revenueAll → revenueAttribution (cents shapes) */
function pickRevenueRows(metrics: any): any[] {
  const trends = metrics?.trends ?? {}
  const primary = Array.isArray(trends.revenue) ? trends.revenue : []
  if (primary.length > 0) return primary
  const all = Array.isArray(trends.revenueAll) ? trends.revenueAll : []
  if (all.length > 0) return all
  return Array.isArray(trends.revenueAttribution) ? trends.revenueAttribution : []
}

/** "$5,320" whole dollars, "$0.73" under a dollar or when cents are asked for */
export function formatMoney(value: number, cents = false): string {
  if (!Number.isFinite(value) || value === 0) return '$0'
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  const digits = cents || value < 1 ? 2 : 0
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`
}

export interface TrafficSummary {
  /** Deduped unique visitors for the period (never a sum of buckets) */
  visitors: number
  /** Total views (pageviews are additive) */
  views: number
  /** Bounce rate in percent, null when there were no visitors */
  bounceRate: number | null
  /** Average time on site in seconds, null when there were no visitors */
  avgTimeOnSiteSeconds: number | null
  /**
   * Deltas against the previous equal-length period (`usePreviousPeriodMetrics`).
   * Null while that loads, on error, or when the previous value was 0: never
   * a fabricated number.
   */
  visitorsDelta: number | null
  viewsDelta: number | null
  /** Percentage points, not relative % */
  bounceDeltaPts: number | null
  timeOnSiteDelta: number | null
  revenue: number
  /**
   * Always null for now: the revenue endpoint only accepts preset periods, so
   * there is no previous-window revenue to compare against.
   */
  revenueDelta: number | null
  hasRevenue: boolean
  /** First and last trend bucket, raw API values */
  rangeStart: string | null
  rangeEnd: string | null
}

/**
 * Headline numbers for the Overview verdict line and the KPI tiles.
 * Pass `previous` (from `usePreviousPeriodMetrics`) to get deltas; without it
 * every delta is null.
 */
export function getTrafficSummary(
  metrics: any,
  showRevenue = true,
  previous: PeriodTotals | null = null,
): TrafficSummary {
  const sessions: any[] = metrics?.trends?.sessions ?? []
  const views: any[] = metrics?.trends?.views ?? []
  const visitors = Number(metrics?.trends?.totalSessions?.[0]?.total_sessions ?? 0) || 0
  const viewTotal = views.reduce((acc: number, v: any) => acc + (v?.count != null ? (+v.count || 0) : 0), 0)
  const bounceCount = Number(metrics?.trends?.bounces?.[0]?.bounce_count ?? 0) || 0
  const sessionTimeMs = (metrics?.trends?.sessionTime ?? []).reduce(
    (acc: number, row: any) => acc + (parseInt(row?.total_session_time) || 0),
    0,
  )
  const bounceRate = visitors > 0 ? (bounceCount / visitors) * 100 : null
  const avgTimeOnSiteSeconds = visitors > 0 ? sessionTimeMs / 1000 / visitors : null
  const revenueRows = pickRevenueRows(metrics)
  const revenue = revenueRows.reduce((acc: number, r: any) => acc + rowCents(r) / 100, 0)
  const hasRevenue = showRevenue && revenue > 0

  return {
    visitors,
    views: viewTotal,
    bounceRate,
    avgTimeOnSiteSeconds,
    visitorsDelta: computePeriodDelta(visitors, previous?.visitors),
    viewsDelta: computePeriodDelta(viewTotal, previous?.views),
    // A bounce count of 0 usually means the metric is missing, not a 0% rate
    bounceDeltaPts: bounceCount > 0 ? computePointDelta(bounceRate, previous?.bounceRate) : null,
    timeOnSiteDelta: sessionTimeMs > 0 ? computePeriodDelta(avgTimeOnSiteSeconds, previous?.avgTimeOnSiteSeconds) : null,
    revenue,
    revenueDelta: null,
    hasRevenue,
    rangeStart: sessions.length ? rawBucket(sessions[0]) || null : null,
    rangeEnd: sessions.length ? rawBucket(sessions[sessions.length - 1]) || null : null,
  }
}



interface TrafficMetricsCardProps {
  dashBoardData: any
  period: string
  className?: string
  showRevenue?: boolean
  revenueLoading?: boolean
  basePath?: string
  /** Previous equal-length period totals; null hides deltas */
  previousTotals?: PeriodTotals | null
  /** Previous window's buckets; shown in the tooltip only */
  previousSeries?: PeriodSeries | null
  /** "the previous 7 days": tooltip text on each delta */
  comparisonPhrase?: string
  /** The previous window itself, for "vs Sep 12 to Sep 18" */
  previousWindow?: PeriodWindow | null
  /** Stripe or Foxy is set up; false shows the quiet "Connect Stripe" line, undefined shows nothing */
  revenueConfigured?: boolean
  /** @deprecated The card always renders its own chrome now */
  withCard?: boolean
  /** @deprecated Kept for call-site compatibility */
  variant?: 'card' | 'flat'
}

interface ChartDataPoint {
  label: string
  /** Selected metric in the current window */
  value: number
  /** Same bucket position in the previous window; null when unknown */
  previous: number | null
  granularity: string
  /** Revenue in the same bucket, for the overlay behind a traffic line */
  revenue?: number | null
}

type FocusSeries = 'visitors' | 'pageViews' | 'revenue'



const SERIES_LABEL: Record<FocusSeries, string> = {
  visitors: 'Unique visitors',
  pageViews: 'Total views',
  revenue: 'Revenue',
}

/** Metric colors are fixed across surfaces (DESIGN.md "Series roles and marks"). */
/**
 * "Wed, Sep 23" (day buckets) or "Sep 23, 3 PM" (hour buckets) for the tooltip
 * header; null when the bucket isn't a date (the single "Period" point).
 */
function formatTooltipDate(raw: string, period: string): string | null {
  const d = new Date(raw)
  if (!raw || Number.isNaN(d.getTime())) return null
  if (['PAST_24_H', '1d', 'YESTERDAY', 'TODAY'].includes(period)) {
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' })
  }
  if (['LAST_12_MONTHS', '12m', 'YEAR_TO_DATE', 'ytd'].includes(period)) {
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

const SERIES_COLOR: Record<FocusSeries, string> = {
  visitors: 'var(--chart-2)',
  pageViews: 'var(--chart-1)',
  revenue: 'var(--chart-revenue)',
}

/** One decimal, dropping a trailing ".0": 1.5, 1 */
function trimDecimal(n: number): string {
  return n.toFixed(1).replace(/\.0$/, '')
}

/** Axis labels as in the design: 1.5K, 1K, 500, 0 */
function formatCompactAxis(value: number): string {
  if (!Number.isFinite(value)) return ''
  if (value === 0) return '0'
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${trimDecimal(value / 1_000_000)}M`
  if (abs >= 1000) return `${trimDecimal(value / 1000)}K`
  if (abs >= 10) return String(Math.round(value))
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

function formatRevenueAxis(value: number): string {
  if (!Number.isFinite(value)) return ''
  if (value === 0) return '$0'
  const abs = Math.abs(value)
  if (abs >= 1000) return `$${trimDecimal(value / 1000)}K`
  if (abs < 1) return `$${value.toFixed(2)}`
  return `$${Math.round(value)}`
}

/** Up reads in ink, down in muted. No arrows, no green (White signal v2). */
function Delta({ value, unit = '%', title }: { value: number | null; unit?: '%' | ' pts'; title?: string }) {
  if (value == null || value === 0 || !Number.isFinite(value)) return null
  return (
    <span
      title={title}
      className={cn(
        'text-xs font-medium leading-4 tabular-nums',
        value > 0 ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      {value > 0 ? '+' : '−'}
      {Math.abs(value)}
      {unit}
    </span>
  )
}

interface KpiTileProps {
  label: string
  value: string
  delta?: number | null
  deltaUnit?: '%' | ' pts'
  deltaTitle?: string
  selected?: boolean
  onSelect?: () => void
  first?: boolean
}

function KpiTile({ label, value, delta, deltaUnit, deltaTitle, selected, onSelect, first }: KpiTileProps) {
  const body = (
    <>
      <span className={cn('text-xs leading-4 whitespace-nowrap', selected ? 'text-foreground' : 'text-muted-foreground')}>
        {label}
      </span>
      <span className="flex items-baseline gap-1.5">
        <span className="text-[22px] leading-7 font-semibold tracking-[-0.02em] text-foreground tabular-nums whitespace-nowrap">
          {value}
        </span>
        {delta != null && <Delta value={delta} unit={deltaUnit} title={deltaTitle} />}
      </span>
    </>
  )
  const cls = cn(
    'flex min-w-[140px] flex-1 basis-0 flex-col gap-1.5 px-[18px] py-4 text-left',
    !first && 'border-l border-border/60',
    selected && 'shadow-[inset_0_-1.5px_0_var(--foreground)]',
  )
  if (onSelect) {
    return (
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={cn(cls, !selected && 'transition-colors hover:bg-muted/50')}
      >
        {body}
      </button>
    )
  }
  return <div className={cls}>{body}</div>
}


export default function TrafficMetricsCard({
  dashBoardData,
  period,
  className,
  showRevenue = true,
  revenueLoading = false,
  basePath = '',
  previousTotals = null,
  previousSeries = null,
  comparisonPhrase,
  previousWindow = null,
  revenueConfigured,
}: TrafficMetricsCardProps) {
  const navigate = useNavigate()
  const [focus, setFocus] = useState<FocusSeries>('visitors')

  const metrics = dashBoardData?.metrics
  const summary = useMemo(
    () => getTrafficSummary(metrics, showRevenue, previousTotals),
    [metrics, showRevenue, previousTotals],
  )
  const deltaTitle = comparisonPhrase ? `Compared with ${comparisonPhrase}` : undefined
  const { visitors: sessionCount, hasRevenue } = summary
  const totalRevenueValue = summary.revenue
  // Revenue focus needs revenue; fall back if it disappears (range or toggle change)
  const activeFocus: FocusSeries = focus === 'revenue' && !hasRevenue ? 'visitors' : focus

  const sessions = useMemo(() => metrics?.trends?.sessions ?? [], [metrics])
  const views = useMemo(() => metrics?.trends?.views ?? [], [metrics])
  const revenueRows = useMemo(() => pickRevenueRows(metrics), [metrics])

  const bounceCount = metrics?.trends?.bounces?.[0]?.bounce_count || 0
  const regularBounceCount = metrics?.trends?.singlePageSessions?.[0]?.bounce_count || 0

  const bounceRate = !bounceCount || !sessionCount
    ? '--'
    : formatNumeric(Math.round((bounceCount / sessionCount) * 100)) + '%'

  const regularBounceRate = !regularBounceCount || !sessionCount
    ? '--'
    : formatNumeric(Math.round((regularBounceCount / sessionCount) * 100)) + '%'

  const totalSessionTimeMs = (metrics?.trends?.sessionTime || [])?.reduce(
    (acc: number, session: any) => acc + (parseInt(session?.total_session_time) || 0),
    0
  )
  const sessionDurationSeconds = sessionCount > 0 ? totalSessionTimeMs / (1000 * sessionCount) : 0
  const sessionDuration = formatDuration(sessionDurationSeconds)

  const revenuePerVisitor = hasRevenue && sessionCount > 0
    ? formatMoney(totalRevenueValue / sessionCount, true)
    : null

  const sessionsWithRevenue = revenueRows.filter((rev: any) => rowCents(rev) > 0).length
  const conversionRate = hasRevenue && sessionCount > 0
    ? formatPercentage((sessionsWithRevenue / sessionCount) * 100)
    : null

  const formatDateLabel = (dateValue: string): string => {
    if (!dateValue) return ''
    try {
      const date = new Date(dateValue)
      if (['PAST_24_H', '1d', 'YESTERDAY', 'TODAY'].includes(period)) {
        return convertUTCToLocalTime(dateValue)
      }
      if (['LAST_12_MONTHS', '12m', 'YEAR_TO_DATE', 'ytd'].includes(period)) {
        return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
      }
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    } catch {
      return dateValue
    }
  }

  /** Map bucket (day, or hour on hourly periods) → dollars */
  const revenueByDay = useMemo(() => {
    const map = new Map<string, number>()
    for (const rev of revenueRows) {
      const key = bucketKey(rawBucket(rev), period)
      if (!key) continue
      map.set(key, (map.get(key) ?? 0) + rowCents(rev) / 100)
    }
    return map
  }, [revenueRows, period])

  /** Visitors or views per bucket, with the previous window's bucket at the same index */
  const trafficData = useMemo((): ChartDataPoint[] => {
    if (!sessions.length) return []
    const prev = activeFocus === 'pageViews' ? previousSeries?.views : previousSeries?.visitors
    return sessions.map((sess: any, index: number) => {
      const dateValue = rawBucket(sess)
      const value = activeFocus === 'pageViews'
        ? parseInt(String(views[index]?.count ?? '0'), 10) || 0
        : parseInt(String(sess?.visitors ?? sess?.count ?? '0'), 10) || 0
      return {
        label: formatDateLabel(dateValue),
        value,
        previous: prev && index < prev.length ? prev[index] : null,
        granularity: dateValue,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, views, previousSeries, activeFocus, period])

  /**
   * Revenue per bucket. Prefer session-aligned X (same shape as traffic); fall
   * back to native buckets, then a single period point. No previous window:
   * the revenue endpoint only takes preset periods.
   */
  const revenueData = useMemo((): ChartDataPoint[] => {
    if (!hasRevenue || totalRevenueValue <= 0) return []

    if (sessions.length > 0) {
      const aligned: ChartDataPoint[] = sessions.map((sess: any, index: number) => {
        const dateValue = rawBucket(sess)
        const key = bucketKey(dateValue, period)
        let dollars = (key ? revenueByDay.get(key) : undefined) ?? 0
        // Index fallback only for parallel arrays of the same length without shared keys;
        // otherwise row i is a different bucket and its money lands in the wrong bar.
        if (dollars <= 0 && revenueByDay.size === 0 && revenueRows.length === sessions.length && revenueRows[index] != null) {
          dollars = rowCents(revenueRows[index]) / 100
        }
        return {
          label: formatDateLabel(dateValue) || key || String(index + 1),
          value: Number.isFinite(dollars) ? dollars : 0,
          previous: null,
          granularity: dateValue || key || String(index),
        }
      })
      if (aligned.some((p) => p.value > 0)) return aligned
      // KPI has money but no per-bucket join: pin the total on the last bucket
      return aligned.map((p, i, arr) => (i === arr.length - 1 ? { ...p, value: totalRevenueValue } : p))
    }

    if (revenueByDay.size > 0) {
      const points = Array.from(revenueByDay.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, dollars]) => ({
          label: formatDateLabel(key) || key,
          value: dollars,
          previous: null,
          granularity: key,
        }))
      if (points.some((p) => p.value > 0)) return points
    }

    return [{ label: 'Period', value: totalRevenueValue, previous: null, granularity: 'total' }]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRevenue, sessions, revenueByDay, revenueRows, totalRevenueValue, period])

  const labels = periodSeriesLabels(period)
  const previousRange = formatWindowRange(previousWindow?.start, previousWindow?.end)
  const currentRange = formatWindowRange(summary.rangeStart, summary.rangeEnd)

  const trafficPoints = useMemo(
    (): PeriodPoint[] =>
      trafficData.map((p) => ({
        label: p.label,
        tooltipLabel: formatTooltipDate(p.granularity, period) || p.label,
        current: p.value,
        previous: p.previous,
      })),
    [trafficData, period],
  )
  const revenuePoints = useMemo(
    (): PeriodPoint[] =>
      revenueData.map((p) => ({
        label: p.label,
        tooltipLabel: formatTooltipDate(p.granularity, period) || p.label,
        current: p.value,
        previous: null,
      })),
    [revenueData, period],
  )

  const trafficTotal = activeFocus === 'pageViews' ? summary.views : sessionCount
  const trafficDelta = formatSignedDelta(
    trafficTotal,
    activeFocus === 'pageViews' ? previousTotals?.views : previousTotals?.visitors,
  )
  const hasPreviousSeries = trafficPoints.some((p) => p.previous != null)
  const trafficLegend = [
    { label: labels.current, color: SERIES_COLOR[activeFocus] },
    ...(hasPreviousSeries ? [{ label: labels.previous, color: PREV_LINE }] : []),
  ]
  // The revenue endpoint takes preset periods only: no previous window, so no ghost bars or delta
  const revenueLegend = [{ label: labels.current, color: SERIES_COLOR.revenue }]
  const showRevenueCard = showRevenue && (hasRevenue || revenueLoading || revenueConfigured === true)

  const goToConnectors = () => {
    navigate(basePath ? `${basePath}/connectors` : '/connectors')
  }

  // Secondary metrics only: visitors, views and revenue live in their chart cards
  const tiles = (
    <div className="flex overflow-x-auto scrollbar-hide">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex min-w-[140px] flex-1 basis-0 cursor-help">
            <KpiTile
              first
              label="Bounce"
              value={bounceRate}
              delta={summary.bounceDeltaPts}
              deltaUnit=" pts"
              deltaTitle={deltaTitle}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-[18em] p-3 text-xs">
          <div className="space-y-2">
            <div>
              <span className="font-medium">True bounce rate</span>
              <p className="mt-1 leading-relaxed text-popover-foreground/70">
                Visitors who left without any interaction.
              </p>
            </div>
            <div className="border-t border-border/60 pt-2">
              <span className="text-popover-foreground/70">Single-page rate: </span>
              <span className="font-medium tabular-nums">{regularBounceRate}</span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
      <KpiTile
        label="Time on site"
        value={sessionDuration}
        delta={summary.timeOnSiteDelta}
        deltaTitle={deltaTitle}
      />
      {hasRevenue && (
        <>
          <KpiTile label="Revenue per visitor" value={revenuePerVisitor ?? '—'} />
          <KpiTile label="Conversion rate" value={conversionRate ?? '—'} />
        </>
      )}
    </div>
  )

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Card className="gap-0 py-0">{tiles}</Card>

      <div className={cn('grid gap-2', showRevenueCard && 'lg:grid-cols-2')}>
        <ChartCard
          title={SERIES_LABEL[activeFocus]}
          headerAction={
            <SegmentedControl
              options={[
                { value: 'visitors', label: 'Visitors' },
                { value: 'pageViews', label: 'Views' },
              ]}
              value={activeFocus === 'pageViews' ? 'pageViews' : 'visitors'}
              onChange={(v) => setFocus(v as FocusSeries)}
            />
          }
          value={formatNumeric(trafficTotal)}
          delta={trafficDelta}
          comparison={trafficDelta && previousRange ? `vs ${previousRange}` : currentRange}
          legend={trafficLegend}
        >
          <TrendLineChart
            data={trafficPoints}
            labels={labels}
            color={SERIES_COLOR[activeFocus]}
            formatAxis={formatCompactAxis}
            overlay={
              trafficPoints.length === 0 ? (
                <ChartEmpty title="No visits in this range yet" body="Try a longer range, or check that tracking is live." />
              ) : undefined
            }
          />
        </ChartCard>

        {showRevenueCard && (
          <ChartCard
            title="Revenue"
            headerAction={<span className="text-xs leading-4 text-muted-foreground">From Stripe</span>}
            value={formatMoney(totalRevenueValue)}
            comparison={currentRange}
            legend={revenueLegend}
            loading={revenueLoading}
          >
            {revenueLoading ? (
              <Skeleton className="h-[200px] w-full rounded-md" />
            ) : (
              <PeriodBarChart
                data={revenuePoints}
                labels={labels}
                color={SERIES_COLOR.revenue}
                showPrevious={false}
                formatValue={(n) => formatMoney(n)}
                formatAxis={formatRevenueAxis}
                overlay={
                  revenuePoints.length === 0 ? (
                    <ChartEmpty title="No payments in this range" body="Payments land about a day after the click." />
                  ) : undefined
                }
              />
            )}
          </ChartCard>
        )}
      </div>

      {showRevenue && revenueConfigured === false && (
        <p className="px-1 text-[13px] leading-4 text-muted-foreground">
          Connect Stripe to see revenue next to traffic.{' '}
          <button type="button" onClick={goToConnectors} className="font-medium text-foreground hover:underline">
            Connect Stripe
          </button>
        </p>
      )}
    </div>
  )
}
